import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Fragment, useEffect, useRef, useState } from 'react'
import { type Board, boardQueryOptions, type Column, isTaskDone } from '@/entities/board'
import { companyListQueryOptions } from '@/entities/company'
import {
  type Employee,
  EmployeeChip,
  employeesQueryOptions,
  meQueryOptions,
} from '@/entities/employee'
import { type Message, taskMessagesQueryOptions } from '@/entities/message'
import { projectListQueryOptions } from '@/entities/project'
import { EntityTagsEditor, entityTagsQueryOptions } from '@/entities/tag'
import {
  boardTasksQueryOptions,
  type Task,
  type TaskCompanyBinding,
  taskQueryOptions,
  taskRef,
} from '@/entities/task'
import {
  runningEntry,
  taskEntriesQueryOptions,
  taskRollupQueryOptions,
} from '@/entities/time-entry'
import { TaskActions } from '@/features/archive-task'
import { AssigneePicker } from '@/features/assign-task'
import { TaskDescription, TaskSteps, TaskTitle } from '@/features/edit-task'
import { EstimateEditButton } from '@/features/estimate-task'
import { ColumnPicker, DoneCheckbox } from '@/features/move-task'
import { FocusMusicButton } from '@/features/play-soundscape'
import {
  MessageActions,
  MessageEditor,
  ThreadComposer,
  useMarkThreadRead,
} from '@/features/post-message'
import { CustomerVisibilityControl } from '@/features/set-customer-visibility'
import { DeadlineEditButton, ReminderSummary } from '@/features/set-deadline'
import { PriorityEditButton } from '@/features/set-priority'
import { TakeButton } from '@/features/take-in-work'
import { WaitingToggle } from '@/features/toggle-waiting'
import { EntriesSection } from '@/features/track-time'
import { ApiError } from '@/shared/api'
import { cn, dueStatus, formatAbsolute, formatDateTime } from '@/shared/lib'
import { AttachmentGallery } from '@/shared/ui/attachment-gallery'
import { Button } from '@/shared/ui/button'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { EstimateMeter } from '@/shared/ui/estimate-meter'
import { Icon } from '@/shared/ui/icon'
import { MarkdownView } from '@/shared/ui/markdown'
import { RiskBadge } from '@/shared/ui/risk-badge'
import { RunningTimer } from '@/shared/ui/running-timer'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { TimeRollup } from '@/shared/ui/time-rollup'
import { ZoneTag } from '@/shared/ui/zone-badge'
import { TaskRelations } from '@/widgets/task-relations'
import { TaskTree } from '@/widgets/task-tree'

export const Route = createFileRoute('/tasks/$taskId')({
  component: TaskCardPage,
  head: () => ({ meta: [{ title: 'Задача · STIT Task' }] }),
})

// zone — что именно в этой секции показано в зоне организации («срок»). Тег стоит
// у заголовка, а не у каждой строки: секция — один столбец сроков.
function SectionTitle({ children, zone }: { children: string; zone?: string }) {
  if (!zone) return <h2 className="label mb-3">{children}</h2>
  return (
    <div className="mb-3 flex items-center gap-2">
      <h2 className="label">{children}</h2>
      <ZoneTag kind="org" prefix={zone} />
    </div>
  )
}

function SkeletonBar({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-control bg-bg-elevated', className)} />
}

function CrumbSeparator() {
  return <span aria-hidden>›</span>
}

// Родитель в крошке: подзадача сама показывает, чья она, и даёт переход наверх.
// Отдельный компонент — свой useQuery ниже guard'ов страницы был бы условным хуком.
function ParentCrumb({ parentId }: { parentId: string }) {
  const parent = useQuery(taskQueryOptions(parentId))
  return (
    <>
      <Link
        to="/tasks/$taskId"
        params={{ taskId: parentId }}
        title="Родительская задача"
        className="inline-flex min-w-0 items-center gap-1.5 transition-colors hover:text-text-primary"
      >
        <Icon name="subtask" size={13} className="shrink-0" />
        <span data-numeric className="mono-num shrink-0 text-text-secondary">
          {taskRef(parentId)}
        </span>
        <span className="min-w-0 truncate">{parent.data?.title ?? '…'}</span>
      </Link>
      <CrumbSeparator />
    </>
  )
}

// === Тред (page-local) ===

function NewDivider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="hairline-accent flex-1" />
      <span className="font-mono text-xs uppercase tracking-wide text-accent">новое</span>
      <span className="hairline-accent flex-1" />
    </div>
  )
}

function MessageItem({
  message,
  authors,
  meId,
  taskId,
  onReply,
  onEdit,
}: {
  message: Message
  authors: Map<string, Employee>
  meId: string | undefined
  taskId: string
  onReply: (m: Message) => void
  onEdit: (m: Message) => void
}) {
  const isReply = message.replyToId !== null
  const mentionsMe = meId != null && message.mentions.includes(meId)
  const author = authors.get(message.authorId)

  if (message.deleted) {
    return (
      <p className={cn('text-sm italic text-text-tertiary', isReply && 'ml-6')}>
        сообщение удалено
      </p>
    )
  }

  return (
    <div
      className={cn(
        'group flex flex-col gap-1.5',
        isReply && 'ml-6 border-l border-border-subtle pl-4',
      )}
    >
      <div className="flex items-center gap-2">
        {isReply && <Icon name="reply" size={14} className="text-text-tertiary" />}
        {author ? (
          <Link
            to="/employees/$employeeId"
            params={{ employeeId: author.id }}
            className="min-w-0 rounded-control transition-opacity hover:opacity-80"
          >
            <EmployeeChip employee={author} />
          </Link>
        ) : (
          <span className="text-sm text-text-secondary">Неизвестный</span>
        )}
        <time
          dateTime={message.createdAt}
          title={formatAbsolute(message.createdAt, true)}
          className="text-xs text-text-tertiary"
        >
          {formatDateTime(message.createdAt)}
        </time>
        {message.editedAt && <span className="text-xs text-text-tertiary">· изменено</span>}
        <span className="ml-auto opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 touch:opacity-100">
          <MessageActions
            taskId={taskId}
            message={message}
            meId={meId}
            onReply={onReply}
            onEdit={onEdit}
          />
        </span>
      </div>

      {message.body && (
        <MarkdownView
          value={message.body}
          className={cn('max-w-[72ch]', mentionsMe && 'border-l-2 border-border-accent pl-3')}
        />
      )}

      <AttachmentGallery attachments={message.attachments} />
    </div>
  )
}

function Thread({
  taskId,
  employees,
  meId,
}: {
  taskId: string
  employees: Employee[]
  meId: string | undefined
}) {
  const { data, isPending, isError, refetch } = useQuery(taskMessagesQueryOptions(taskId))
  const { mutate: markThreadRead } = useMarkThreadRead(taskId)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  // Отметку прочтения откладываем до ухода с экрана — пока читаешь, разделитель
  // «— новое —» держится (readByMe в кэше не меняется до unmount).
  const hadUnread = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (data?.some((m) => !m.readByMe && m.authorId !== meId)) hadUnread.current = true
  }, [data, meId])

  useEffect(() => {
    return () => {
      if (hadUnread.current) markThreadRead()
    }
  }, [markThreadRead])

  const authors = new Map(employees.map((e) => [e.id, e]))
  const firstUnread = data ? data.findIndex((m) => !m.readByMe && m.authorId !== meId) : -1
  const count = data?.length ?? 0

  // Лента растёт вниз, поэтому непрочитанные — всегда в конце: скролл к низу и
  // показывает их, и «— новое —» вместе с ними. Дёргаем по длине — на своё
  // отправленное сообщение тоже.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || count === 0) return
    el.scrollTop = el.scrollHeight
  }, [count])

  if (isPending) return <Spinner label="Загрузка обсуждения" />
  if (isError) {
    return (
      <div className="flex items-center gap-3 text-sm text-text-tertiary">
        Не удалось загрузить обсуждение
        <button
          type="button"
          onClick={() => {
            void refetch()
          }}
          className="text-text-secondary underline transition-colors hover:text-text-primary"
        >
          Повторить
        </button>
      </div>
    )
  }

  // Переписка живёт в своей рамке: скроллится только лента, композер закреплён у
  // нижней кромки. Иначе длинный тред растит страницу и уносит поле ввода вниз.
  return (
    // На телефоне ограничение высоты снято: скроллер внутри скролла страницы —
    // ловушка для пальца, а места на узком экране и так мало.
    <div className="flex max-h-none flex-col rounded-card border border-border-subtle sm:max-h-[32rem]">
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4"
      >
        {data.length === 0 ? (
          <p className="text-sm text-text-tertiary">Опишите задачу или приложите скриншот.</p>
        ) : (
          data.map((m, i) => (
            <Fragment key={m.id}>
              {i === firstUnread && <NewDivider />}
              {editingId === m.id ? (
                <MessageEditor
                  taskId={taskId}
                  message={m}
                  employees={employees}
                  onDone={() => setEditingId(null)}
                />
              ) : (
                <MessageItem
                  message={m}
                  authors={authors}
                  meId={meId}
                  taskId={taskId}
                  onReply={setReplyTo}
                  onEdit={(msg) => setEditingId(msg.id)}
                />
              )}
            </Fragment>
          ))
        )}
      </div>
      <div className="shrink-0 border-t border-border-subtle bg-bg-sunken px-4 py-3">
        <ThreadComposer
          taskId={taskId}
          meId={meId}
          employees={employees}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
        />
      </div>
    </div>
  )
}

// Обсуждение сворачивается: на задаче с длинной перепиской оно иначе оттесняет
// подзадачи и связи. Счётчик виден и в свёрнутом виде.
function ThreadSection({
  taskId,
  employees,
  meId,
}: {
  taskId: string
  employees: Employee[]
  meId: string | undefined
}) {
  const [open, setOpen] = useState(true)
  const { data } = useQuery(taskMessagesQueryOptions(taskId))
  const count = data?.length ?? 0

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="label inline-flex items-center gap-1.5 transition-colors hover:text-text-primary"
        >
          <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
          Обсуждение
          {count > 0 && (
            <span data-numeric className="mono-num text-text-tertiary">
              {count}
            </span>
          )}
        </button>
        {/* Отметки времени реплик — столбец моментов: зона над ним, а не в каждой. */}
        {open && count > 0 && <ZoneTag kind="device" />}
      </div>
      {open && <Thread taskId={taskId} employees={employees} meId={meId} />}
    </section>
  )
}

// === Правая колонка: свойства + время + оценка (page-local) ===

// Строка панели свойств: подпись фиксированной ширины, значение — своим контролом.
// Grid, а не flex: значения выравниваются по колонке, даже когда подписи разной длины.
// min-h держит одинаковую высоту строк, чем бы значение ни было (чип, чип+аватар,
// плоский текст), — иначе вертикальный ритм скачет от контрола к контролу.
function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-8 grid-cols-[5rem_1fr] items-center gap-x-2 text-sm">
      <span className="text-xs text-text-tertiary">{label}</span>
      {/* -mx-2 гасит px-2 самих контролов: их текст встаёт по левому краю колонки,
          а каретки/карандаши — по правому краю панели. */}
      <span className="-mx-2 flex min-w-0 items-center gap-1.5 text-text-secondary">
        {children}
      </span>
    </div>
  )
}

// Строка служебного подвала: та же колонка подписи, что и у PropRow, — подписи
// «Назначил/Автор/Создана» встают под «Колонка/Исполнитель», а не своей вертикалью.
function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5rem_1fr] items-center gap-x-2">
      <span>{label}</span>
      {/* nowrap: строка факта не разъезжается на две — длинное имя обрежется само
          (chip с truncate), а время назначения остаётся на своей строке. */}
      <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">{children}</span>
    </div>
  )
}

// Свойства задачи — то, что раньше висело мета-строкой в шапке. Здесь они
// читаются столбцом «подпись → значение», а редактируются теми же контролами.
// Служебное (автор, кто назначил, даты) — приглушённым подвалом: оно нужно редко.
function PropertiesPanel({
  task,
  board,
  columns,
  employees,
  employeesById,
  binding,
  companyName,
}: {
  task: Task
  // Доска нужна тумблеру видимости: пометка работает только на открытой доске.
  board: Board | undefined
  columns: Column[]
  employees: Employee[]
  employeesById: Map<string, Employee>
  binding: TaskCompanyBinding
  companyName: string | undefined
}) {
  const assignedBy = task.assignedBy ? employeesById.get(task.assignedBy) : undefined
  const author = task.createdBy ? employeesById.get(task.createdBy) : undefined

  return (
    <section>
      <SectionTitle>Свойства</SectionTitle>
      <div className="flex flex-col gap-0.5 rounded-card bg-bg-surface px-4 py-3 ring-1 ring-border-subtle">
        <PropRow label="Колонка">
          <ColumnPicker task={task} columns={columns} />
        </PropRow>
        <PropRow label="Исполнитель">
          <AssigneePicker task={task} employees={employees} />
        </PropRow>
        <PropRow label="Приоритет">
          <PriorityEditButton task={task} />
        </PropRow>
        <PropRow label="Срок">
          <DeadlineEditButton task={task} muted={isTaskDone(task, columns)} />
        </PropRow>
        {/* Свои напоминания — следом за сроком, тем же слотом. Пустой набор
            строки не даёт: там работает лестница студии. */}
        {task.reminders && task.reminders.length > 0 && (
          <PropRow label="Напомним">
            <span className="px-2">
              <ReminderSummary task={task} />
            </span>
          </PropRow>
        )}

        <div className="my-2 hairline" />

        {/* Открытость наружу компании — не рядовое свойство: отдельной строкой,
            с именем компании и аудитом «кто открыл». */}
        <CustomerVisibilityControl
          task={task}
          board={board}
          binding={binding}
          companyName={companyName}
        />

        <div className="my-2 hairline" />

        <div className="flex flex-col gap-1.5 text-xs text-text-tertiary">
          {/* Отметки аудита — столбец моментов: зона подписана над ним один раз. */}
          <div className="flex items-center gap-2">
            <span className="label">Отметки</span>
            <ZoneTag kind="device" />
          </div>
          {task.assigneeId && (
            <MetaRow label="Назначил">
              {assignedBy ? <EmployeeChip employee={assignedBy} /> : <span>—</span>}
              {task.assignedAt && (
                <time dateTime={task.assignedAt} title={formatAbsolute(task.assignedAt, true)}>
                  {formatDateTime(task.assignedAt)}
                </time>
              )}
            </MetaRow>
          )}
          <MetaRow label="Автор">
            {author ? (
              <EmployeeChip employee={author} />
            ) : task.createdByCustomer ? (
              <span>{task.createdByCustomer} · заказчик</span>
            ) : (
              <span>—</span>
            )}
          </MetaRow>
          <MetaRow label="Создана">
            <time dateTime={task.createdAt} title={formatAbsolute(task.createdAt, true)}>
              {formatDateTime(task.createdAt)}
            </time>
          </MetaRow>
          {/* Правка — своя строка: с полной датой и временем две отметки в одну
              строку не встают, а панель узкая. */}
          {task.updatedAt !== task.createdAt && (
            <MetaRow label="Изменена">
              <time dateTime={task.updatedAt} title={formatAbsolute(task.updatedAt, true)}>
                {formatDateTime(task.updatedAt)}
              </time>
            </MetaRow>
          )}
        </div>
      </div>
    </section>
  )
}

function TimePanel({
  task,
  meId,
  employees,
}: {
  task: Task
  meId: string | undefined
  employees: Map<string, Employee>
}) {
  const rollup = useQuery(taskRollupQueryOptions(task.id))
  const entries = useQuery(taskEntriesQueryOptions(task.id))
  const running = entries.data ? runningEntry(entries.data) : undefined
  const actualHours =
    rollup.data && rollup.data.totalSeconds > 0 ? rollup.data.totalSeconds / 3600 : null
  const hasEstimate = task.estimateHours != null || task.complexity != null

  return (
    <div className="flex flex-col gap-6">
      <section>
        <SectionTitle>Время</SectionTitle>
        {rollup.isPending ? (
          <Spinner label="Загрузка времени" />
        ) : rollup.isError ? (
          <p className="text-sm text-text-tertiary">Не удалось загрузить время</p>
        ) : (
          <div className="flex flex-col gap-2">
            <TimeRollup
              totalSeconds={rollup.data.totalSeconds}
              ownSeconds={rollup.data.ownSeconds}
              size="md"
            />
            {running && <RunningTimer startedAt={running.startedAt} label="идёт" />}
          </div>
        )}
        <div className="mt-3">
          <EntriesSection taskId={task.id} meId={meId} employees={employees} />
        </div>
      </section>

      <div className="hairline" />

      <section>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="label">Оценка</h2>
          {hasEstimate && <EstimateEditButton task={task} />}
        </div>
        {hasEstimate ? (
          <div className="rounded-card bg-bg-surface px-4 py-3 ring-1 ring-border-subtle">
            <EstimateMeter
              estimateHours={task.estimateHours}
              complexity={task.complexity}
              actualHours={actualHours}
              actualComplexity={task.actualComplexity}
            />
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 rounded-card bg-bg-surface px-4 py-4 ring-1 ring-border-subtle">
            <p className="text-sm text-text-tertiary">
              Задача ещё не оценена — задайте план по часам и сложности.
            </p>
            <EstimateEditButton task={task} />
          </div>
        )}
      </section>

      <div className="hairline" />

      <section>
        <h2 className="label mb-3">Теги</h2>
        <TaskTags taskId={task.id} />
      </section>
    </div>
  )
}

// Теги задачи (00-architecture § 5): читаются отдельной ручкой /api/tags/task/{id},
// не встроены в read-view. Клик по чипу уводит в поиск, отфильтрованный по тегу.
function TaskTags({ taskId }: { taskId: string }) {
  const navigate = useNavigate()
  const tags = useQuery(entityTagsQueryOptions('task', taskId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return (
    <EntityTagsEditor
      entityKind="task"
      entityId={taskId}
      tags={tags.data ?? []}
      onTagClick={(tag) => void navigate({ to: '/search', search: { tag } })}
    />
  )
}

// === Страница ===

function TaskCardPage() {
  const { taskId } = Route.useParams()
  const taskQ = useQuery(taskQueryOptions(taskId))
  const boardId = taskQ.data?.boardId

  const boardQ = useQuery({ ...boardQueryOptions(boardId ?? ''), enabled: Boolean(boardId) })
  const tasksQ = useQuery({ ...boardTasksQueryOptions(boardId ?? ''), enabled: Boolean(boardId) })
  const employeesQ = useQuery(employeesQueryOptions)
  const meQ = useQuery(meQueryOptions)
  // Чья это задача с точки зрения клиента: доска знает только project_id, поэтому
  // компанию собираем из уже загружаемых справочников — отдельной ручки на это нет.
  const projectsQ = useQuery(projectListQueryOptions(true, true))
  const companiesQ = useQuery(companyListQueryOptions(true, true))

  if (taskQ.isPending) {
    return (
      <div className="container-x flex flex-col gap-8 px-4 py-8 sm:px-6 sm:py-10">
        <SkeletonBar className="h-9 w-2/3" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-3">
            <SkeletonBar className="h-6 w-full" />
            <SkeletonBar className="h-6 w-5/6" />
            <SkeletonBar className="h-6 w-4/6" />
          </div>
          <SkeletonBar className="h-40 w-full" />
        </div>
      </div>
    )
  }

  if (taskQ.isError) {
    const notFound = taskQ.error instanceof ApiError && taskQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Задача недоступна' : 'Не удалось загрузить задачу'}
        message={
          notFound
            ? 'Возможно, она удалена или у вас нет доступа.'
            : 'Попробуйте обновить страницу.'
        }
      >
        <Button
          variant="secondary"
          onClick={() => {
            void taskQ.refetch()
          }}
        >
          Повторить
        </Button>
        <Link
          to="/"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К моим задачам
        </Link>
      </StatusScreen>
    )
  }

  const task = taskQ.data
  const board = boardQ.data
  const column = board?.columns.find((c) => c.id === task.columnId)
  const employees = new Map((employeesQ.data ?? []).map((e) => [e.id, e]))
  // Выполненная задача не бывает просроченной: колонка done снимает и бейдж, и
  // тревожный тон срока (то же правило, что в списках и на Ганте).
  const done = isTaskDone(task, board?.columns ?? [])
  const overdue =
    !done && task.dueDate != null && dueStatus(task.dueDate, undefined, task.dueTime) === 'overdue'
  const project = projectsQ.data?.find((p) => p.id === board?.projectId)
  const company = companiesQ.data?.find((c) => c.id === project?.companyId)
  const binding: TaskCompanyBinding = {
    companyId: project?.companyId ?? null,
    projectId: board?.projectId ?? null,
  }

  return (
    // На широком экране колонки скроллятся по отдельности: длинное обсуждение
    // слева не должно угонять вверх свойства и время справа. Ниже lg колонки
    // складываются в одну — там скролл общий, страничный.
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10 lg:flex lg:h-full lg:flex-col lg:overflow-hidden">
      {/* «Компания › Проект › Доска › #ref» — где живёт задача. Каждое звено ведёт
          туда, где эта сущность живёт целиком; проект открывается страницей своей
          компании с подсветкой карточки. */}
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-text-tertiary">
        {company && (
          <>
            <Link
              to="/companies/$companyId"
              params={{ companyId: company.id }}
              className="max-w-[24ch] truncate transition-colors hover:text-text-primary"
            >
              {company.name}
            </Link>
            <CrumbSeparator />
          </>
        )}
        {project && (
          <>
            <Link
              to="/companies/$companyId"
              params={{ companyId: project.companyId }}
              search={{ project: project.id }}
              className="max-w-[24ch] truncate transition-colors hover:text-text-primary"
            >
              {project.name}
            </Link>
            <CrumbSeparator />
          </>
        )}
        {board && (
          <>
            <Link
              to="/boards/$boardId"
              params={{ boardId: board.id }}
              title="К доске"
              className="inline-flex min-w-0 items-center gap-1.5 transition-colors hover:text-text-primary"
            >
              <DepartmentBadges names={board.departments} variant="dot" />
              <span className="min-w-0 truncate text-text-secondary">{board.name}</span>
            </Link>
            <CrumbSeparator />
          </>
        )}
        {task.parentId && <ParentCrumb parentId={task.parentId} />}
        <span data-numeric className="mono-num text-text-secondary">
          {taskRef(task.id)}
        </span>
        {/* Архив и удаление — на дальнем краю той же строки: это действия над
            задачей целиком, они не должны соседствовать с рабочими кнопками. */}
        <span className="ml-auto">
          <TaskActions task={task} column={column} tasks={tasksQ.data ?? []} compact />
        </span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:min-h-0 lg:flex-1 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-10 lg:min-h-0 lg:overflow-y-auto lg:pr-3 lg:[scrollbar-gutter:stable]">
          {/* Заголовок владеет всей шириной, действия — своей строкой под ним:
              иначе кластер кнопок переносится вместе с длинным названием и
              «Взять в работу» уезжает то вправо, то вниз. */}
          <header className="flex flex-col gap-4">
            <div className="flex min-w-0 items-start gap-3">
              {task.type === 'bug' && (
                <Icon name="bug" size={24} className="mt-1 shrink-0 text-text-tertiary" />
              )}
              <TaskTitle task={task} />
            </div>

            {/* Два яруса: сверху — что делать с задачей («взять» и звук под неё),
                снизу — как её пометить. Иначе «выполнено» и «ждёт ответа» тонут в
                одном ряду с главным действием. */}
            <div className="flex flex-col items-start gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <TakeButton
                  task={task}
                  columns={board?.columns ?? []}
                  meId={meQ.data?.id}
                  employees={employeesQ.data ?? []}
                />
                <FocusMusicButton />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {board && (
                  <DoneCheckbox
                    task={{ id: task.id, boardId: task.boardId, columnId: task.columnId }}
                    columns={board.columns}
                    showLabel
                    className="rounded-pill border border-border-default px-2.5"
                  />
                )}
                <WaitingToggle task={task} columns={board?.columns ?? []} />
                {overdue && <RiskBadge kind="overdue" />}
                {task.archived && (
                  <span className="inline-flex items-center gap-1.5 rounded-control bg-bg-elevated px-2 py-0.5 text-xs text-text-tertiary">
                    <Icon name="archive" size={12} />в архиве
                  </span>
                )}
              </div>

              {/* Связи — сразу под пометками: чем задача держится и кого держит,
                  видно вместе с её статусом, а не в дальней колонке. */}
              <div className="w-full">
                <TaskRelations taskId={task.id} boardId={task.boardId} />
              </div>
            </div>
          </header>

          <TaskDescription task={task} />

          {task.type === 'bug' && <TaskSteps task={task} />}

          <section>
            <SectionTitle zone="срок">Подзадачи</SectionTitle>
            {tasksQ.isPending ? (
              <Spinner label="Загрузка подзадач" />
            ) : tasksQ.isError ? (
              <p className="text-sm text-text-tertiary">Не удалось загрузить подзадачи</p>
            ) : (
              <TaskTree
                key={taskId}
                rootId={taskId}
                tasks={
                  tasksQ.data.some((t) => t.id === task.id) ? tasksQ.data : [...tasksQ.data, task]
                }
                columns={board?.columns ?? []}
                employees={employeesQ.data ?? []}
              />
            )}
          </section>

          <ThreadSection taskId={taskId} employees={employeesQ.data ?? []} meId={meQ.data?.id} />
        </div>

        {/* На узком экране правая колонка встаёт под контент — «Свойства» идут
            первыми: статус и исполнитель нужнее времени и тегов. */}
        <aside className="flex flex-col gap-6 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          <PropertiesPanel
            task={task}
            board={board}
            columns={board?.columns ?? []}
            employees={employeesQ.data ?? []}
            employeesById={employees}
            binding={binding}
            companyName={company?.name}
          />
          <div className="hairline" />
          <TimePanel task={task} meId={meQ.data?.id} employees={employees} />
        </aside>
      </div>
    </div>
  )
}
